import {
  Typography,
  makeStyles,
  Button,
  FeatureChip,
  Grid,
  Hidden,
  IconButton,
  Link,
  useIsMobile,
  useMediaQuery
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { ClientLogo, DropdownSelect, useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CancelIcon from '@material-ui/icons/Cancel'
import MenuIcon from '@material-ui/icons/Menu'
import { useSearchLocationsQuery } from '@paintscout/api'
import { getFeature, getFeatures, getObjectLabels } from '@paintscout/util/builder'
import { BackLink } from '@paintscout/react-quote'
import classnames from 'classnames'
import find from 'lodash/find'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import pageMargins from 'src/components/styles/snippets/pageMargins'
import { MASQUERADE_BANNER_HEIGHT } from './headerHeight'
import striptags from 'striptags'
import { getDunningStatusMessage } from 'src/util/dunningMessages'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { clearMasqueradeCompanyId, getMasqueradeCompanyId, setMasqueradeCompanyId } from 'shared/graphql/masquerade'
import { getCompanyAccentColor, getCompanyAccentSurface } from 'shared/util/masquerade'
import MultiTabChecker from '../MultiTabChecker'

const SPACE_BETWEEN = 'space-between'
const TRIAL_BANNER_CLASS = 'trial-banner'
const BILLING_PLAN_PATH = '/settings?section=billing-plan'
import DropdownNewButton from '../DropdownNewButton'
import UserButtons from './UserButtons'
import { useNavContext } from './SideNav'

const useStyles = makeStyles<Theme, { isMasquerading?: boolean; sticky?: boolean }>(
  (theme) => ({
    root: ({ isMasquerading, sticky }) => ({
      boxShadow: 'none',
      borderBottom: `1px solid ${theme.paintscout.palette.brand?.Neutral?.P90}`,
      borderTop: !isMasquerading ? `1px solid ${theme.paintscout.palette.brand?.Neutral?.P90}` : 'none',
      // height: theme.paintscout.header.height.md,
      // `sticky` is set by the page (see NavContext.setStickyHeader): the header keeps its place in
      // the flow, then pins to the top once it would scroll away. Any banner above it scrolls off
      // first, so `top: 0` is the right resting place.
      position: sticky ? 'sticky' : 'relative',
      ...(sticky ? { top: 0 } : {}),
      zIndex: 3,
      [theme.breakpoints.down('sm')]: {
        // height: theme.paintscout.header.height.sm
      },
      '@media print': {
        display: 'none'
      }
    }),
    headerContentWrapper: {
      background: theme.palette.secondary.main,
      transition: `${theme.transitions.duration.standard}ms background`
    },
    headerContent: {
      ...pageMargins(theme, 'xl'),
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      boxSizing: 'content-box',
      // height: theme.paintscout.header.height.md,
      [theme.breakpoints.down('sm')]: {
        // height: theme.paintscout.header.height.sm,
        paddingTop: 'env(safe-area-inset-top)'
      },
      [theme.breakpoints.down('xs')]: {
        width: '96%'
      }
    },
    headerContentChild: {
      display: 'flex',
      alignItems: 'center',
      columnGap: theme.spacing(1)
    },
    companyLogo: {
      height: theme.typography.pxToRem(50),
      width: 'auto'
    },
    companyLogoNew: {
      marginTop: theme.spacing(0.5)
    },
    drawerButton: {
      color: theme.paintscout.palette.brand?.Neutral?.P40 || theme.palette.secondary.contrastText
    },
    userButtons: {},
    trialBannerWrapper: {
      position: 'relative',
      zIndex: 3,
      backgroundColor: theme.paintscout.palette.secondary?.['260']?.C95
    },
    paymentBannerWrapper: {
      backgroundColor: theme.palette.error.light,
      borderBottom: '1px solid rgba(0, 0, 0, .12)'
    },
    banner: {
      ...pageMargins(theme, 'xl'),
      padding: theme.spacing(2, 0)
    },
    trialBanner: {
      columnGap: theme.spacing(6),
      minHeight: '60px',
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'wrap',
        textAlign: 'center',
        justifyContent: 'center',
        columnGap: 32
      },
      rowGap: 12,
      color: theme.palette.text.primary,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        marginRight: '15px'
      },
      '& p': {
        lineHeight: 1.5
      },
      '& button': {
        // marginLeft: '15px',
        // for some reason without this the buttons horizontal padding is overlapped by the text
        minWidth: 'auto',
        [theme.breakpoints.down('xs')]: {
          // margin: '10px auto 0 auto'
        }
      },
      '&$trialBanner': {
        padding: theme.spacing(1, 0)
      }
    },
    trialBannerActions: {
      // marginLeft: theme.spacing(6)
    },
    paymentBanner: {
      [theme.breakpoints.down('md')]: {
        flexWrap: 'wrap',
        textAlign: 'center',
        justifyContent: 'center'
      },
      color: theme.palette.error.dark,
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      '& svg': {
        marginRight: '15px'
      },
      '& p': {
        lineHeight: 1.5
      },
      '& button': {
        marginLeft: '15px',
        // for some reason without this the buttons horizontal padding is overlapped by the text
        minWidth: 'auto',
        [theme.breakpoints.down('md')]: {
          margin: '10px auto 0 auto'
        }
      }
    },
    bookDemoText: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      fontWeight: 500,
      textDecoration: 'none'
    },
    backLink: {
      color: theme.paintscout.palette.brand.Neutral.P30,
      '& svg': {
        fontSize: '1em !important'
      },
      '@media print': {
        display: 'none'
      }
    },
    // --- estimateNext breadcrumb (← | Estimates › <contact>) ---
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      minWidth: 0
    },
    // Back arrow: styled like an icon button (the BackLink renders an <a>), navigates to the previous
    // location via the session history tracker.
    breadcrumbBack: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.paintscout.palette.brand.Neutral.P40,
      borderRadius: theme.borderRadius?.sm ?? 4,
      padding: theme.spacing(0.5),
      lineHeight: 0,
      textDecoration: 'none',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      },
      '& svg': {
        fontSize: 20
      }
    },
    breadcrumbDivider: {
      width: 1,
      height: 20,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P85,
      margin: theme.spacing(0, 0.5)
    },
    breadcrumbList: {
      color: theme.paintscout.palette.brand.Neutral.P50,
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 500,
      textDecoration: 'none',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P30,
        textDecoration: 'underline'
      }
    },
    breadcrumbSep: {
      color: theme.paintscout.palette.brand.Neutral.P70,
      fontSize: 18,
      flexShrink: 0
    },
    breadcrumbCurrent: {
      color: theme.paintscout.palette.brand.Neutral.P30,
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    switcher: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: SPACE_BETWEEN,
      boxSizing: 'border-box',
      minHeight: MASQUERADE_BANNER_HEIGHT,
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75)
    },
    // Corporate users switching between their own franchise locations — this is their normal working
    // mode, not an alarming state, so it keeps the original brand treatment.
    switcherCorporate: {
      backgroundColor: theme.paintscout.palette.primary,
      color: theme.palette.common.white
    },
    // Superadmin/support inside a paying customer's account.
    //
    // The surface is normally overridden inline with a tint of that customer's brand color, so you can
    // tell which account you're in at a glance. These values are the fallback for an account whose
    // `primaryColor` can't be read — caution tones, so such an account still doesn't look like ordinary
    // app chrome. Text stays a dark neutral either way: a brand-colored tint can be any hue, so
    // nothing here may depend on it for contrast.
    switcherSupport: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.text.primary,
      borderBottomWidth: 2,
      borderBottomStyle: 'solid',
      borderBottomColor: theme.palette.warning.main
    },
    switcherStopButton: {
      textTransform: 'none',
      color: 'inherit',
      padding: theme.spacing(0, 1),
      '& svg': {
        fontSize: '1.1em !important'
      }
    },
    // The account you're actually in. Leads the bar — it's the information; the exit action sits
    // opposite it.
    accountIdentity: {
      display: 'flex',
      alignItems: 'center',
      columnGap: theme.spacing(1),
      minWidth: 0
    },
    // White backing for the customer's logo. Customer artwork is frequently white or light on
    // transparent, so it needs a guaranteed light surface to sit on regardless of the banner colour.
    accountLogoChip: {
      display: 'flex',
      alignItems: 'center',
      flex: '0 0 auto',
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.borderRadius.sm,
      padding: theme.spacing(0.25, 0.5),
      maxWidth: 96,
      [theme.breakpoints.down('xs')]: {
        display: 'none'
      }
    },
    accountLogo: {
      display: 'block',
      // fits inside the slim bar; `contain` so wide and tall logos both scale without distortion
      maxHeight: theme.spacing(2.5),
      maxWidth: '100%',
      width: 'auto',
      objectFit: 'contain'
    },
    accountName: {
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    accountId: {
      opacity: 0.75,
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('xs')]: {
        display: 'none'
      }
    },
    accountWarningIcon: {
      fontSize: '1.15rem !important',
      flex: '0 0 auto'
    },
    switcherContainer: {
      ...pageMargins(theme, 'xl'),
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      paddingLeft: -theme.spacing(2),
      paddingRight: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        justifyContent: SPACE_BETWEEN
      }
    },
    corporateBackLink: {
      marginRight: theme.spacing(8),
      marginLeft: -theme.spacing(1.5),
      // was 18px — scaled down to sit in the slimmer bar without crowding it
      fontSize: theme.typography.pxToRem(15),
      paddingLeft: 0,
      color: theme.palette.common.white,
      [theme.breakpoints.down('lg')]: {
        marginLeft: -theme.spacing(1)
      },
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(10)
      },
      [theme.breakpoints.down('sm')]: {
        marginLeft: -theme.spacing(1),
        marginRight: theme.spacing(0)
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0
      }
    },
    itemDiv: {
      display: 'flex',
      alignItems: 'center'
    },
    locationDropdown: {
      marginLeft: theme.spacing(1),
      maxHeight: theme.spacing(5),
      width: '500px',
      [theme.breakpoints.down('sm')]: {
        width: '300px'
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0
      },
      [theme.breakpoints.down(350)]: {
        width: '280px'
      }
    }
  }),
  { name: 'Header' }
)

function Header() {
  const location = useLocation()

  const { options } = useClientOptions()
  const features = getFeatures({ options })

  const {
    isTrial,
    trialExpired,
    isSubscribed,
    isSuperadmin,
    isSupport,
    paymentExpiryNotice,
    preferences,
    isCorporate,
    isMasquerading,
    isChurned,
    dunningStatus
  } = useUser()
  const { permissionToUpdateBilling } = useUserPermissions()
  const { stickyHeader } = useNavContext()
  const classes = useStyles({ isMasquerading, sticky: stickyHeader })
  const hasProfileCommunications = !getFeature({
    options,
    preferences,
    path: 'settingsCommunications.enabled'
  })
  const isBillingPage = location.pathname.startsWith('/settings')
  const showPaymentExpiryNotice =
    paymentExpiryNotice && isSubscribed && (!isSuperadmin || !isSupport) && features?.billing?.enabled
  const showInactiveBanner = !isSubscribed && (!isSuperadmin || !isSupport) && !(isChurned && isBillingPage)
  // Note: !isSuperadmin && !isSupport (not the || used by showPaymentExpiryNotice/showInactiveBanner
  // above) — this banner should hide for either role, not only when both apply at once.
  const showDunningBanner =
    (dunningStatus === 'restricted' || dunningStatus === 'locked') && !isSuperadmin && !isSupport

  const showFollowUpErrorBanner = hasProfileCommunications
    ? Object.values(preferences?.emails?.followUps?.values ?? {}).filter((followUp) => {
        const smsMessage = striptags(followUp?.smsMessage ?? '')
        return followUp.sendText && (smsMessage.length === 0 || smsMessage.length > 95)
      }).length > 0
    : Object.values(options.options?.emails?.followUps?.values ?? {}).filter((followUp) => {
        const smsMessage = striptags(followUp?.smsMessage ?? '')
        return followUp.sendText && (smsMessage.length === 0 || smsMessage.length > 95)
      }).length > 0 && permissionToUpdateBilling

  const hideNewButtonForTrialDashboard = isTrial && trialExpired

  const isOnPresentationNewPage = location.pathname.startsWith('/new-presentation')
  if (isOnPresentationNewPage) {
    return null
  }

  return (
    <>
      {/* Only show TrialBanner if trial is active (not expired) - expired trials show dedicated dashboards */}
      {isTrial && !trialExpired && <TrialBanner classes={classes} />}
      <MainHeader
        classes={classes}
        isCorporate={isCorporate}
        isMasquerading={isMasquerading}
        hideNewButtonForTrialDashboard={hideNewButtonForTrialDashboard}
      />
      {showInactiveBanner && <InactiveBanner classes={classes} />}
      {showPaymentExpiryNotice && <PaymentExpiryBanner classes={classes} />}
      {showDunningBanner && <DunningBanner classes={classes} status={dunningStatus as 'restricted' | 'locked'} />}
      {showFollowUpErrorBanner && (
        <FollowUpErrorBanner classes={classes} hasProfileCommunications={hasProfileCommunications} />
      )}
      <MultiTabChecker />
    </>
  )
}

interface HeaderProps {
  classes: any
  isCorporate: boolean
  isMasquerading: boolean
  hideNewButtonForTrialDashboard: boolean
}

export function shouldRenderNewButton({
  hideNewButtonForTrialDashboard,
  isCorporate,
  isMasquerading
}: {
  hideNewButtonForTrialDashboard: boolean
  isCorporate: boolean
  isMasquerading: boolean
}) {
  return !hideNewButtonForTrialDashboard && !(isCorporate && !isMasquerading)
}

function MainHeader(props: HeaderProps) {
  const { isCorporate, isMasquerading, classes, hideNewButtonForTrialDashboard } = props
  const { setOpen, pageSubtitle, pageChip, pageActions } = useNavContext()
  const xsDown = useMediaQuery('xs')
  const location = useLocation()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { estimateNext } = useFlags()
  const shouldShowNewButton = shouldRenderNewButton({
    hideNewButtonForTrialDashboard,
    isCorporate,
    isMasquerading
  })

  // On the redesigned estimate/invoice editor (estimateNext), the plain "Estimate: <name>" title is
  // replaced by a breadcrumb (back arrow | Estimates › <contact>). Only when the flag is on so the
  // classic editor keeps its existing header.
  const showBreadcrumb =
    estimateNext &&
    ['/quotes/edit', '/quotes/new', '/invoices/edit', '/invoices/new'].some((p) => location.pathname.includes(p))

  const pathToTitleMap: [string, string][] = [
    ['/dashboard', 'Dashboard'],
    ['/deals/edit', 'Deal'],
    ['/deals', 'Pipeline'],
    ['/quotes/edit', objectLabels.quote.value],
    ['/quotes', objectLabels.quote.plural],
    ['/invoices/edit', 'Invoice'],
    ['/invoices', 'Invoices'],
    ['/tasks/', 'Tasks'],
    ['/events', 'Calendar'],
    ['/events/?tab=calendar', 'Calendar'],
    ['/events/?tab=map', 'Calendar'],
    ['/scheduling', 'Calendar'],
    ['/scheduling/?tab=calendar', 'Calendar'],
    ['/scheduling/?tab=map', 'Calendar'],
    ['/people/contacts', 'Contacts'],
    ['/people/contact', 'Contact'],
    ['/people/companies', 'Companies'],
    ['/people/companies/*', 'Company'],
    ['/reports', 'Reports'],
    ['/locations', objectLabels.locations.plural],
    ['/integrate', 'Integrate'],
    ['/settings', 'Settings'],
    ['/profile', 'Profile']
  ]

  const pathToNewButtonMap: [string, string][] = [
    ['/dashboard/', 'all'],
    ['/deals/board', 'deal'],
    ['/deals/list', 'deal'],
    ['/quotes/', 'quote'],
    ['/invoices/', 'invoice'],
    ['/events/', 'event'],
    ['/tasks/', 'task'],
    ['/people/contacts/', 'contact'],
    ['/people/companies/', 'company']
  ]

  const newButtonElement = shouldShowNewButton ? renderNewButton(location.pathname) : null

  function getFriendlyTitle(pathname: string) {
    const match = pathToTitleMap.reduce<[string, string] | null>((best, entry) => {
      const [pattern] = entry
      const matches = pattern.includes('*')
        ? // eslint-disable-next-line security/detect-non-literal-regexp
          new RegExp('^' + pattern.replace(/\*/g, '.+')).test(pathname)
        : pathname.startsWith(pattern)
      if (matches && (!best || pattern.length > best[0].length)) {
        return entry
      }
      return best
    }, null)
    const title = match ? match[1] : ''

    return pageSubtitle ? (
      <Typography variant={'h4'}>
        <span style={{ fontWeight: 600 }}>{title}:</span> {` ${pageSubtitle}`}
      </Typography>
    ) : (
      <Typography variant={'h4'} style={{ fontWeight: 600 }}>
        {title}
      </Typography>
    )
  }

  function renderNewButton(pathname: string) {
    const cleanPath = pathname.split('?')[0].replace(/\/+$/, '') // remove trailing slashes
    // eslint-disable-next-line security/detect-non-literal-regexp
    const match = pathToNewButtonMap.find(([path]) => new RegExp(`^${path.replace(/\/+$/, '')}/?$`).test(cleanPath))

    if (!match) return null
    if (match[1] === 'all') {
      return <DropdownNewButton />
    } else {
      return <DropdownNewButton onlyFor={match[1]} />
    }
  }

  return (
    <header className={classes.root}>
      {isCorporate && isMasquerading && <CorporateMasqueradeSwitcher />}
      {!isCorporate && isMasquerading && <MasqueradeSwitcher />}
      <div className={classes.headerContentWrapper}>
        <div className={classes.headerContent}>
          <div className={classes.headerContentChild}>
            <Hidden lgUp>
              <IconButton
                classes={{ root: classes.drawerButton }}
                edge={xsDown ? null : 'start'}
                onClick={() => setOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Hidden>
            <Hidden smDown>
              {showBreadcrumb ? (
                <EstimateBreadcrumb classes={classes} pageSubtitle={pageSubtitle} />
              ) : (
                getFriendlyTitle(`${location.pathname}${location.search}`)
              )}
            </Hidden>
            {pageChip && <FeatureChip {...pageChip} collapseSize={'sm'} />}
            <QuoteBackLink />
          </div>
          <div className={classes.headerContentChild}>
            {newButtonElement}
            {/* Page-supplied header controls (e.g. estimateNext AI Assistant + sidebar toggle) sit
                to the left of the profile menu. */}
            {pageActions}
            <UserButtons classes={{ root: classes.userButtons }} />
          </div>
        </div>
      </div>
    </header>
  )
}

function MasqueradeSwitcher() {
  const { isMasquerading, user } = useUser()
  const { clientId, options } = useClientOptions()
  const classes = useStyles({ isMasquerading })
  const history = useHistory()

  // companyDetails from appDocs is authoritative for the masqueraded company (see
  // buildUserForProvider), so this is the customer's name — not the staff user's own company.
  const companyName = user?.app_metadata?.company?.name
  // The banner takes its surface from the customer's own brand color, so you can tell which account
  // you're in at a glance. Null for a color that can't be read, in which case the CSS fallback
  // (warning tones) stands — see getCompanyAccentSurface.
  const accentSurface = getCompanyAccentSurface(getCompanyAccentColor({ options, companyId: clientId }))
  // gate the chip on the logo actually existing — ClientLogo renders null without one, which would
  // otherwise leave an empty white square sitting in the banner
  const hasLogo = !!options?.options?.logo?.cloudinaryId

  return (
    <div
      className={classnames(classes.switcher, classes.switcherSupport)}
      style={
        accentSurface
          ? { backgroundColor: accentSurface.background, borderBottomColor: accentSurface.border }
          : undefined
      }
    >
      <Grid container spacing={1} className={classes.switcherContainer}>
        <Grid item className={classes.accountIdentity}>
          <ErrorOutlineIcon className={classes.accountWarningIcon} />
          {/*
            The customer's own logo — the fastest way to tell whose account this is. Sits on a
            white chip because a logo is arbitrary customer artwork: plenty are white-on-transparent
            and would vanish into a tinted banner. Not every account has one, hence the name and id
            beside it.
          */}
          {hasLogo && (
            <span className={classes.accountLogoChip}>
              <ClientLogo className={classes.accountLogo} />
            </span>
          )}
          <Typography variant={'body2'} className={classes.accountName} title={companyName ?? undefined}>
            {companyName ?? 'Unknown company'}
          </Typography>
          <Typography variant={'caption'} className={classes.accountId}>
            {clientId}
          </Typography>
        </Grid>
        <Grid item className={classes.itemDiv}>
          <Button
            classes={{ label: classes.switcherStopButton }}
            variant={'text'}
            size={'small'}
            onClick={() => {
              // per-tab: other tabs keep whatever account they're in
              clearMasqueradeCompanyId()
              history.push('/')
              window.location.reload()
            }}
            startIcon={<CancelIcon />}
          >
            <Typography variant={'body2'}>Stop Masquerading</Typography>
          </Button>
        </Grid>
      </Grid>
    </div>
  )
}

function CorporateMasqueradeSwitcher() {
  const { isMasquerading } = useUser()
  const classes = useStyles({ isMasquerading })
  const history = useHistory()

  const masqueradeCompanyId = getMasqueradeCompanyId()

  const { data, error: _error } = useSearchLocationsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    variables: {
      query: '*:*'
    }
  })

  if (!masqueradeCompanyId) {
    return null
  }

  const locations = (data?.searchLocations?.rows ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((row) => {
      return {
        label: row.name,
        value: row._id
      }
    })

  return (
    <div className={classnames(classes.switcher, classes.switcherCorporate)}>
      <Grid container spacing={1} className={classes.switcherContainer}>
        <Grid item className={classes.itemDiv} xs={12} sm={6} md={4} style={{ marginLeft: '-5px' }}>
          <Button
            classes={{ label: classes.corporateBackLink }}
            variant={'text'}
            onClick={async () => {
              clearMasqueradeCompanyId()
              history.push('/')
            }}
            startIcon={<ArrowBackIcon />}
          >
            <Typography variant={'body1'} style={{ textTransform: 'none' }}>
              Back to Corporate Dashboard
            </Typography>
          </Button>
        </Grid>
        <Grid item className={classes.itemDiv}>
          <Hidden xsDown>
            <Typography variant={'body1'}>Viewing:</Typography>
          </Hidden>
          <DropdownSelect
            className={classes.locationDropdown}
            options={locations}
            value={find(locations, { value: masqueradeCompanyId })}
            variant={'single'}
            onChange={async (option) => {
              if (option.value) {
                setMasqueradeCompanyId(option.value)
                history.push('/')
              }
            }}
          />
        </Grid>
      </Grid>
    </div>
  )
}

/**
 * Breadcrumb shown in the main header on the redesigned estimate/invoice editor (estimateNext):
 * a back arrow (returns to the previous location via the session history tracker), a divider, the
 * list link (Estimates / Invoices → its list page), and the current document's contact name.
 */
function EstimateBreadcrumb({ classes, pageSubtitle }: { classes: any; pageSubtitle?: string }) {
  const location = useLocation()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })

  const isInvoice = location.pathname.includes('/invoices')
  const listLabel = isInvoice ? 'Invoices' : objectLabels.quote.plural
  const listPath = isInvoice ? '/invoices' : '/quotes'

  return (
    <div className={classes.breadcrumb}>
      {/* No `to` → BackLink navigates to the previously-visited location (session history). */}
      <BackLink className={classes.breadcrumbBack} aria-label="Back">
        {() => <ArrowBackIcon fontSize="small" />}
      </BackLink>
      <span className={classes.breadcrumbDivider} aria-hidden />
      <Link to={listPath} className={classes.breadcrumbList}>
        {listLabel}
      </Link>
      {pageSubtitle && (
        <>
          <ChevronRightIcon className={classes.breadcrumbSep} />
          <Typography component="span" className={classes.breadcrumbCurrent}>
            {pageSubtitle}
          </Typography>
        </>
      )}
    </div>
  )
}

function QuoteBackLink() {
  const { isMasquerading } = useUser()
  const classes = useStyles({ isMasquerading })
  // const { options } = useClientOptions()
  // const objectLabels = getObjectLabels({ options })
  const history = useHistory()
  const { hideBackLink } = useNavContext()

  const getLink = (_pathname: string) => {
    // if (!pathname) {
    //   return 'Back'
    // } else if (pathname.includes('/quotes/edit') || pathname.includes('/quotes/new')) {
    //   return `Back to ${objectLabels.quote.value}`
    // } else if (pathname.includes('/quotes')) {
    //   return `Back to ${objectLabels.quote.plural}`
    // } else if (pathname.includes('/invoices/edit') || pathname.includes('/invoices/new')) {
    //   return 'Back to Invoice'
    // } else if (pathname.includes('/invoices')) {
    //   return 'Back to Invoices'
    // } else if (pathname.includes('/settings')) {
    //   return 'Back to Settings'
    // } else if (pathname.includes('/people/contact')) {
    //   return 'Back to Contact'
    // } else if (pathname.includes('/people')) {
    //   return 'Back to People'
    // }

    return 'Back'
  }

  const showLink =
    !hideBackLink &&
    ['/quotes/edit', '/quotes/new', '/invoices/edit', '/invoices/new'].some((link) =>
      history.location.pathname.includes(link)
    )

  if (showLink) {
    return (
      <Hidden mdUp>
        <BackLink>
          {(prevLocation) => (
            <Button className={classes.backLink} variant="text" size="small" startIcon={<ArrowBackIcon />}>
              {getLink(prevLocation?.pathname)}
            </Button>
          )}
        </BackLink>
      </Hidden>
    )
  }
  return null
}

function FollowUpErrorBanner(props: { classes: any; hasProfileCommunications: boolean }) {
  const { classes, hasProfileCommunications } = props

  return (
    <div className={classes.paymentBannerWrapper}>
      <div className={classnames(classes.paymentBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Hidden mdDown>
          <ErrorOutlineIcon />
        </Hidden>
        <div>
          <Typography variant="body1">
            We’ve updated our SMS follow-up requirements, and some of your messages need adjustment.
          </Typography>
          {/* <br /> */}
          <Typography variant="body1">
            <>
              Please click
              <Link
                style={{ color: 'red' }}
                to={
                  hasProfileCommunications
                    ? '/profile?category=follow-ups'
                    : '/settings?section=communications-follow-ups'
                }
              >
                {' '}
                here
              </Link>{' '}
              to apply fixes.
            </>
          </Typography>
        </div>
      </div>
    </div>
  )
}

function PaymentExpiryBanner(props: { classes: any }) {
  const { permissionToUpdateBilling } = useUserPermissions()
  const { classes } = props

  return (
    <div className={classes.paymentBannerWrapper}>
      <div className={classnames(classes.paymentBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Hidden mdDown>
          <ErrorOutlineIcon />
        </Hidden>
        {!permissionToUpdateBilling ? (
          <Typography variant="body1">
            <>Your payment method is expiring soon. Please contact your admin user.</>
          </Typography>
        ) : (
          <Typography variant="body1">
            <>
              Your payment method is expiring soon. Please update your payment method{' '}
              <Link style={{ color: 'red' }} to={BILLING_PLAN_PATH}>
                {' '}
                here
              </Link>
            </>
          </Typography>
        )}
      </div>
    </div>
  )
}

// Payment-failure dunning banner (see #21609). Server enforces the actual gate on quote
// create/send regardless — this is just so the customer understands why the "New" button is
// partially or fully disabled, and how to fix it.
function DunningBanner(props: { classes: any; status: 'restricted' | 'locked' }) {
  const { permissionToUpdateBilling } = useUserPermissions()
  const { options } = useClientOptions()
  const { classes, status } = props
  const message = getDunningStatusMessage({ status, options })

  return (
    <div className={classes.paymentBannerWrapper}>
      <div className={classnames(classes.paymentBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Hidden mdDown>
          <ErrorOutlineIcon />
        </Hidden>
        {!permissionToUpdateBilling ? (
          <Typography variant="body1">{`${message} Please contact your admin user.`}</Typography>
        ) : (
          <Typography variant="body1">
            <>
              {`${message} Update your payment method `}
              <Link style={{ color: 'red' }} to={BILLING_PLAN_PATH}>
                here
              </Link>
            </>
          </Typography>
        )}
      </div>
    </div>
  )
}

function TrialBanner(props: { classes: any }) {
  const { trialExpired, trialExpiry, updateTrialExpired, user } = useUser()
  const { permissionToUpdateBilling } = useUserPermissions()
  const leadSource = user.app_metadata?.company?.leadSource || ''
  const mobile = useIsMobile()
  const { crmVisible } = useFlags()
  const { classes } = props
  moment.relativeTimeThreshold('h', 49)
  const timeRemaining = moment(trialExpiry).fromNow(true)
  const showSwCoupon = leadSource === 'Sherwin Williams'
  const couponMessage = showSwCoupon
    ? `${mobile ? '\n' : ' '}Select your plan to claim your $100 Sherwin-Williams coupon.`
    : ''

  // feeds the current time on each render to UserProvider
  // so it can set trialExpired accurately and send it throughout the app
  useEffect(() => {
    updateTrialExpired(Date.now())
  })

  function getTrialMessage() {
    if (trialExpired) {
      return permissionToUpdateBilling
        ? `Your trial has ended. ${couponMessage ? '' : 'Select a plan to get started.'}${couponMessage}`
        : `Your trial has ended. Contact an admin on your team to get started.`
    }

    return `You have ${timeRemaining} left on your PaintScout${crmVisible ? ' Sales + Operations' : ''} trial.${couponMessage}`
  }

  return (
    <div className={classes.trialBannerWrapper}>
      <div className={classnames(classes.trialBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
          {getTrialMessage()}
        </Typography>
        {permissionToUpdateBilling && (
          <div className={classes.trialBannerActions}>
            <Link to={BILLING_PLAN_PATH} tabIndex={-1}>
              <Button variant={'dialog'}>Select Plan</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

function InactiveBanner(props: { classes: any }) {
  const { classes } = props
  const { permissionToUpdateBilling } = useUserPermissions()

  return (
    <div className={classes.trialBannerWrapper}>
      <div className={classnames(classes.trialBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Typography variant="body1">Your subscription is currently inactive.</Typography>
        {permissionToUpdateBilling && (
          <Link to={BILLING_PLAN_PATH} tabIndex={-1}>
            <Button disableElevation variant="dialog">
              Select Plan
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}

declare global {
  interface Window {
    Intercom: any
  }
}

export default Header
